import { useTranslation } from 'react-i18next'

import {
  PrimaryButton,
  TertiaryButton,
  Tooltip,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactAlert } from '~/components/common/CompactAlert'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { translateInterpolatedMessage } from '~/models/planSubmission/interpolatedMessage'
import type { SubmitDisabledReason } from '~/pages/ProcurementPlanDetail/models/canSubmit'
import type {
  SubmitGuidance,
  SubmitGuidanceAction,
} from '~/pages/ProcurementPlanDetail/models/submitGuidance'

type Props = {
  guidance: SubmitGuidance
  /** ボタン押下時の処理 (動作は action.kind で分ける)。 */
  onAction: (action: SubmitGuidanceAction) => void
}

/**
 * 提出情報バー直下の誘導メッセージ (design `#sd-next-action` の型:
 * アイコン + 見出し + 本文 + ボタンの alert。AC-PLC-020-2 / 055 / 056)。
 * 見出し・文言とボタンの有無は `submitGuidance.ts` の対応表が決める。
 *
 * UI 辞書 components/alert-placement.md §4 の組み方: 見出しは `title`、本文は `description`、
 * ボタンだけを `children` に入れて右端に置く (Compact Alert。ページ最上部の `notices` に渡す)。
 * ボタンは、業務を進める主操作 (提出・再作成) は Primary sm、画面の移動 (「最新の計画を開く」・
 * 表へ・不整合へ) は Tertiary sm。
 */
export const SubmitGuidanceAlert = ({
  guidance,
  onAction,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const action = guidance.action
  const message = translateInterpolatedMessage(t, guidance.message)
  return (
    <CompactAlert
      // 見出しが無い誘導は本文を見出しとして出す (本文だけの Alert にしない)
      description={guidance.title === undefined ? undefined : message}
      status={guidance.status}
      title={
        guidance.title === undefined
          ? message
          : guidance.title.causeKey === undefined
            ? t(guidance.title.key)
            : t(guidance.title.key, { cause: t(guidance.title.causeKey) })
      }
    >
      {action !== undefined && (
        <GuidanceActionButton action={action} onAction={onAction} />
      )}
    </CompactAlert>
  )
}

/**
 * 誘導のボタン。無効で理由の文がある時は、ヘッダーの「提出」 と同じ理由をツールチップで
 * 出す (design `.ecl-disabled-wrap`。disabled の button は hover を受けないので span で包む)。
 * 食い違いのバナー (`SpotTradeResultMismatchAlert`) の「計画を作り直す」 も同じ無効表示にする。
 */
export const GuidanceActionButton = ({
  action,
  onAction,
}: {
  action: SubmitGuidanceAction
  onAction: (action: SubmitGuidanceAction) => void
}): React.ReactElement => {
  const { t } = useTranslation()
  const { readonlyReason } = useWorkflowLockReasons()
  const { disabled, reason } = buttonState(action)
  // 業務を進める主操作 (提出・再作成) は Primary sm、画面の移動 (最新の計画を開く・表へ・
  // 不整合へ) は Tertiary sm
  const ActionButton =
    action.kind === 'submit' || action.kind === 'recreate'
      ? PrimaryButton
      : TertiaryButton
  const button = (
    <ActionButton
      flexShrink={0}
      disabled={disabled}
      onClick={() => {
        onAction(action)
      }}
      size="sm"
    >
      {t(action.labelKey)}
    </ActionButton>
  )
  // 閲覧のみ (完了済み・削除済み) が確定している時は画面固有の理由より優先して出す
  if (disabled && readonlyReason !== undefined) {
    return <ButtonTooltip content={readonlyReason}>{button}</ButtonTooltip>
  }
  if (reason === undefined) return button
  return (
    <Tooltip content={t(reason.key, reason.values)}>
      <span>{button}</span>
    </Tooltip>
  )
}

function buttonState(action: SubmitGuidanceAction): {
  disabled: boolean
  reason: SubmitDisabledReason | undefined
} {
  switch (action.kind) {
    case 'submit':
      return {
        disabled: action.disabled,
        reason: action.disabled ? action.disabledReason : undefined,
      }
    case 'recreate':
      return { disabled: action.disabled, reason: undefined }
    case 'openLatest':
    case 'scrollToTable':
    case 'showMismatch':
      return { disabled: false, reason: undefined }
  }
}
